import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { DeactivateWorkerConfirmation } from '@/modules/workforce/Crew/components/Modals/DeactivateWorkerConfirmation';
import { ISuppliers } from '@/modules/workforce/Crew/types';
import { Avatar, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import PartialListWithTooltip from '@/shared/components/molecules/PartialListWithTooltip';
import { Item } from '@/shared/components/molecules/PartialListWithTooltip/IPartialListWithTooltip';
import routes from '@/shared/constant/routes';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Edit from '~/images/shared/edit.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import QRCode from '~/images/shared/scan-barcode-filled.svg';
import Trash from '~/images/shared/TrashWithoutColor.svg';
import Worker from '~/images/shared/Worker.svg';

const columnHelper =
  createColumnHelper<ISuppliers.IGetSupplierTeamMemberData>();

// TODO: add onclick function for QR code

export const useSupplierTeamHook = () => {
  const {
    query: { id },
    push,
  } = useRouter();
  const [isDemoFarm] = useStore((store) => store.isDemoFarm);
  const { openModal } = useModal();

  const supplierTeamTableColumns = (
    t: TFunction<'common', undefined>,
    onClickQrHandler: (row: ISuppliers.IGetSupplierTeamMemberData) => void,
    status: 'ACTIVE' | 'INACTIVE',
    supplierId?: string
  ) => {
    const list = [];
    list.push(
      columnHelper.accessor('name', {
        cell: ({ getValue, row: { original } }) => (
          <Link
            href={`${routes.suppliers}/${supplierId}/worker/${original?.workerId}`}
          >
            <div className='flex gap-2'>
              <div>
                <div className='h-48 w-48'>
                  <Avatar
                    size='lg'
                    className='w-48 bg-surface-0'
                    src={original.profilePicture}
                    alt={original.name}
                    icon={<Worker />}
                    variant='single'
                  />
                </div>
              </div>
              <div className='ml-5 flex w-full flex-col items-start gap-3 '>
                <Typography weight='bold' variant='subtitle'>
                  {getValue()}
                </Typography>
                <Typography variant='subtitle2'>
                  {t('date-creation')}:{' '}
                  {format(new Date(original.createdAt), 'dd/MM/yyyy')}
                </Typography>
              </div>
            </div>
          </Link>
        ),
        header: () => <Typography variant='subtitle1'>{t('name')}</Typography>,
      })
    );

    list.push(
      columnHelper.accessor('jobs', {
        cell: ({ getValue }) =>
          getValue()?.length !== 0 ? (
            <PartialListWithTooltip
              limit={1}
              items={getValue().map((job) => {
                return {
                  description: job.jobName,
                  imageURL: job.cropImage,
                } as Item;
              })}
            />
          ) : (
            <span className='flex w-full items-center justify-start gap-5'>
              -
            </span>
          ),
        header: () => <div className='justify-start'>{t('experience')}</div>,
      })
    );
    if (status === 'ACTIVE') {
      list.push(
        columnHelper.display({
          id: 'qrCode',
          cell: ({ row: { original } }) => (
            <div className='flex items-center justify-end gap-24'>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  onClickQrHandler(original);
                }}
                className='font-semibold flex cursor-pointer items-center justify-center'
              >
                <BaseIcon
                  svgClasses='cursor-pointer text-primary-default'
                  size='md'
                >
                  <QRCode />
                </BaseIcon>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  push(
                    `${
                      routes.suppliers
                    }/${id}/worker/${original?.workerId?.toString()}?isEditing=true`
                  );
                }}
                className='font-semibold flex cursor-pointer items-center justify-center'
              >
                <BaseIcon
                  svgClasses='cursor-pointer text-primary-default'
                  size='md'
                >
                  <Edit />
                </BaseIcon>
              </div>
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className='font-semibold cursor-pointer items-center justify-center gap-1'
              >
                <DotedMenu
                  list={[
                    {
                      title: (
                        <div className='flex items-center gap-2 text-error-400'>
                          <Trash width='24' height='24' />
                          <span>{t('deactivate')}</span>
                        </div>
                      ),
                      id: '0',
                      action: () =>
                        openModal(
                          isDemoFarm ? (
                            <DemoCTA />
                          ) : (
                            <DeactivateWorkerConfirmation
                              workerId={original.workerId}
                              crewId={Number(id)}
                              workerName={original.name}
                            />
                          ),
                          {
                            containerClassName: 'max-w-md',
                          }
                        ),
                    },
                  ]}
                  getTitle={(item) => item?.title}
                  getId={(item) => item?.id}
                  getAction={(item) => item.action}
                  onOpen={() => {
                    //
                  }}
                  DotsMenuIcon={<DotsMenu className='text-24' />}
                />
              </div>
            </div>
          ),
        })
      );
    }
    return list;
  };
  return { supplierTeamTableColumns };
};
